import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { useCurrentDate } from '@/hooks/current-date-provider';
import { MODE_AIR } from '@/lib/constants';
import { getLocationByIdState } from '@/state/locations';
import { ScheduleFrequencyInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { createScheduleSearchPageUrl } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { Bar } from './bar';
import { createScheduleDateRange, getScheduleConfig, trimFrequencyToRange } from './utils';

export interface OptionFrequencyChartProps {
  originId: string;
  destinationId: string;
  mode: SegmentModeType;
  schedule: ScheduleFrequencyInterface;
  carrierIds?: string[];
  allowDateSearch?: boolean;
}

const OptionFrequencyChart = ({
  originId,
  destinationId,
  mode,
  schedule,
  carrierIds,
  allowDateSearch = false,
}: OptionFrequencyChartProps) => {
  const currentDate = useCurrentDate();
  const origin = useRecoilValue(getLocationByIdState(originId));
  const destination = useRecoilValue(getLocationByIdState(destinationId));

  const originTimezone = origin.timezoneName;
  const scheduleType = schedule.type || 'day';
  const chartConfig = getScheduleConfig(scheduleType);

  // Creates a date range with today being in the middle-ish
  const dateRange = createScheduleDateRange(scheduleType, originTimezone, currentDate);
  // Removes dates from frequency data that don't overlay our desired date-range
  const trimmedFrequency = trimFrequencyToRange(schedule.frequency, dateRange);
  // Get the highest frequency value
  const maxValue = !isEmpty(trimmedFrequency) ? Math.max(...Object.values(trimmedFrequency)) : 0;
  // Round to the nearest even number for visual aesthetics
  const maxValueRnd = maxValue > 1 ? 2 * Math.round(maxValue / 2) : 1;

  const hasSchedules = !isEmpty(schedule.frequency);

  const getScheduleUrlParams = (date: Date) => {
    const params = {
      origin,
      destination,
      mode,
      dateRange: { dateFrom: date },
    };
    if (mode === MODE_AIR && carrierIds !== undefined) {
      return { ...params, filters: { carrierIds } };
    }

    return params;
  };

  const handleBarClick = (date: Date) => {
    if (allowDateSearch) {
      const url = createScheduleSearchPageUrl(getScheduleUrlParams(date));
      window.open(url, '_blank')?.focus();
      // Analytics Event
      trackUser.event('Search Schedules Using Bar Chart', { date });
    }
  };

  const { base, chart, chartInner, chartAxisTop, chartAxisMid, chartAxisBottom, chartAxisLabel, bars, emptyMsg } =
    styles();

  return (
    <article className={base()}>
      <header className="mb-4">
        <Heading as="h3" variant="h5" className="font-medium">
          {chartConfig.chartLabel}
        </Heading>
      </header>
      <div className={chart()}>
        <div className={chartInner()}>
          <span className={chartAxisTop()}>
            <span className={chartAxisLabel()}>{maxValueRnd || ''}</span>
          </span>
          <span className={chartAxisMid()}>
            {maxValueRnd > 1 && <span className={chartAxisLabel()}>{maxValueRnd / 2 || ''}</span>}
          </span>
          <span className={chartAxisBottom()} />
          <div className={bars()} data-testid="bars">
            {dateRange.map((day) => {
              const scheduleValue = Number(trimmedFrequency?.[day.key]) || 0;
              return (
                <Bar
                  maxHeight={maxValueRnd}
                  value={scheduleValue}
                  allowDateSearch={allowDateSearch}
                  onClick={handleBarClick}
                  {...day}
                  {...chartConfig}
                  key={day.key}
                />
              );
            })}
          </div>
          {!hasSchedules && (
            <div className={emptyMsg()}>
              <Text className="m-0 bg-white px-2 py-1" variant="sm">
                No active schedules available
              </Text>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const chartAxisStyles = 'h-0 border-t top-0 border-dashed border-[#e1e1e1] absolute left-0 right-0';
const chartAxisLabelStyles = 'absolute top-1.5 left-0 text-xxs text-grey-500 uppercase leading-none';

const styles = tv({
  slots: {
    base: 'relative text-baseSm',
    chart: 'flex pb-8',
    chartInner: 'relative max-w-screen-sm pl-6',
    chartAxisTop: `${chartAxisStyles} top-px`,
    chartAxisMid: `${chartAxisStyles} top-1/2`,
    chartAxisBottom: `${chartAxisStyles} top-full -mt-0.5`,
    chartAxisLabel: `${chartAxisLabelStyles}`,
    bars: 'grid h-16 auto-cols-[minmax(8px,_28px)] grid-flow-col items-end gap-2',
    emptyMsg: 'absolute left-0 right-0 top-4 grid place-items-center italic text-grey-600',
  },
});

export { OptionFrequencyChart };
